import { useEffect, useState } from 'react';
import { FilterOutlined, LockOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  Empty,
  Flex,
  Grid,
  Pagination,
  Result,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ModuleThemeProvider,
  moduleLabel,
  useEstadoDeLista,
  type ModuleKey,
  type SelecaoDeAssuntos,
} from '@titularis/ui';
import type { MotivoDeBloqueio } from '@titularis/api-client';
import { api } from '../api';
import { registrarTela } from '../analytics';
import {
  PainelDeFiltros,
  type CampoLista,
  type FiltrosDeEstudo,
} from '../components/PainelDeFiltros';
import { useAbrirOferta, useBloqueio } from '../components/Oferta';
import { moduloEmPreparo } from '../grandesModulos';
import { filtrosDaConsulta } from '../filtrosDoEstudo';
import { QuestaoCard } from '../components/QuestaoCard';
import { CabecalhoDoModulo } from '../components/CabecalhoDoModulo';

/**
 * Estudo: filtros e lista de questões.
 *
 * Mobile first, como o escopo pede: no celular os filtros vivem numa gaveta e a
 * tela inteira é do enunciado; em tela grande eles ficam num painel à vista, na
 * estrutura da referência do escopo.
 *
 * A subárvore inteira roda dentro do tema do módulo, então ENAC, Objetivas e
 * Discursivas parecem produtos próprios sem deixarem de ser a mesma plataforma.
 */
const TAMANHOS_DE_PAGINA = [10, 20, 50];
const POR_PAGINA_PADRAO = 10;

const formatador = new Intl.NumberFormat('pt-BR');

export function EstudarPage() {
  const [searchParams] = useSearchParams();
  const { ler, filtrar, irParaPagina } = useEstadoDeLista();
  const telas = Grid.useBreakpoint();
  const queryClient = useQueryClient();

  const module = (searchParams.get('module') as ModuleKey | null) ?? 'enac';

  /*
   * Rascunho é o que está nos campos; aplicados é o que virou consulta.
   *
   * Separar os dois é o que permite montar um filtro com calma sem a lista
   * pular a cada campo mexido - e, com centenas de questões por busca, sem
   * disparar uma consulta a cada clique.
   *
   * Só os **aplicados** vão para a URL. Rascunho é digitação em andamento: não
   * é endereço de nada, e escrevê-lo faria a barra piscar a cada campo mexido.
   * O que se manda por link é o que se está vendo.
   */
  const aplicados: FiltrosDeEstudo = {
    years: ler.lista('years'),
    editions: ler.lista('editions'),
    boardIds: ler.lista('boardIds'),
    notaryOfficeIds: ler.lista('notaryOfficeIds'),
    disciplineId: ler.texto('disciplineId'),
    subjectIds: ler.lista('subjectIds'),
    subjectDetailIds: ler.lista('subjectDetailIds'),
    essayKinds: ler.lista('essayKinds'),
  };

  /*
   * **O artigo de lei que a Legislação Comentada mandou.** Não entra nos
   * `aplicados` porque não é filtro da gaveta: ninguém o escolhe aqui, ele chega
   * pelo link da etiqueta `12 questões na prova` e sai quando se limpa.
   */
  const leiCitada = ler.texto('legislation');
  const artigoCitado = ler.texto('artigo');

  /*
   * **As questões que um link escolheu pelo id** - o de cada dia do cronograma
   * de estudos. Também não é filtro da gaveta: chega pelo link, sai no "Ver
   * todas", e a lista vem na ordem em que o link as pôs.
   */
  const questoesDoLink = ler.lista('questoes');

  const pagina = ler.pagina();
  // Tamanho fora da lista vira o padrao: a URL e digitavel, e pageSize=99999
  // seria uma consulta que ninguem escolheu na tela.
  const porPagina = TAMANHOS_DE_PAGINA.includes(Number(ler.texto('porPagina')))
    ? Number(ler.texto('porPagina'))
    : POR_PAGINA_PADRAO;

  const [rascunho, setRascunho] = useState<FiltrosDeEstudo>(aplicados);
  const [gavetaAberta, setGavetaAberta] = useState(false);
  /*
   * O bloqueio abre a oferta, que mora no provider e não nesta tela. A origem
   * viaja junto porque ela escolhe a frase do modal: parar de responder e parar
   * de ler a resolução são momentos diferentes na cabeça de quem estuda, e o
   * segundo é o que vende.
   */
  const navegar = useNavigate();
  const bloquear = useBloqueio();
  const abrirOferta = useAbrirOferta();

  /*
   * Voltar e avançar no navegador trocam os aplicados por fora. Sem isto os
   * campos continuariam mostrando o rascunho antigo enquanto a lista já é
   * outra - a tela contando duas histórias ao mesmo tempo.
   *
   * É o padrão de ajustar estado durante a renderização, e não num efeito:
   * o React descarta esta renderização e refaz com o valor novo, sem passar
   * pela tela intermediária errada.
   */
  const assinatura = JSON.stringify(aplicados);
  const [assinaturaDoRascunho, setAssinaturaDoRascunho] = useState(assinatura);
  if (assinatura !== assinaturaDoRascunho) {
    setAssinaturaDoRascunho(assinatura);
    setRascunho(aplicados);
  }

  useEffect(() => {
    registrarTela(`/estudar?module=${module}`);
  }, [module]);

  const consulta = useQuery({
    queryKey: [
      'study-questions',
      { module, aplicados, pagina, porPagina, leiCitada, artigoCitado, questoesDoLink },
    ],
    // A regra do que vale para cada módulo mora em `filtrosDoEstudo`, com teste.
    queryFn: () =>
      api.listStudyQuestions({
        ...filtrosDaConsulta(module, aplicados, {
          leiCitada,
          artigoCitado,
          questoes: questoesDoLink,
        }),
        page: pagina,
        pageSize: porPagina,
      }),
    // Sem isto a lista some por um instante a cada troca de página: com muitas
    // páginas, vira um pisca constante.
    placeholderData: (anterior) => anterior,
  });

  const status = useQuery({ queryKey: ['study-status'], queryFn: () => api.studyStatus() });

  const mudarLista = (campo: CampoLista, valores: string[]) => {
    setRascunho((atuais) => ({ ...atuais, [campo]: valores }));
  };

  const mudarDisciplina = (valor?: string) => {
    setRascunho((atuais) => {
      const proximos: FiltrosDeEstudo = { ...atuais, disciplineId: valor };
      // Trocar a disciplina invalida os assuntos e delimitações, que são dela.
      delete proximos.subjectIds;
      delete proximos.subjectDetailIds;
      return proximos;
    });
  };

  const mudarAssuntos = (selecao: SelecaoDeAssuntos) => {
    setRascunho((atuais) => ({
      ...atuais,
      subjectIds: selecao.subjectIds,
      subjectDetailIds: selecao.subjectDetailIds,
    }));
  };

  /** Os filtros aplicados viram parâmetros da URL, e a URL vira a consulta. */
  const escreverFiltros = (filtros: FiltrosDeEstudo) => {
    filtrar({
      years: filtros.years,
      editions: filtros.editions,
      boardIds: filtros.boardIds,
      notaryOfficeIds: filtros.notaryOfficeIds,
      disciplineId: filtros.disciplineId,
      subjectIds: filtros.subjectIds,
      subjectDetailIds: filtros.subjectDetailIds,
      essayKinds: filtros.essayKinds,
    });
  };

  // Fechar um chip refaz a busca sem ele; o rascunho acompanha pela
  // ressincronia acima, já que os aplicados mudaram.
  const reaplicarSem = (ajuste: (filtros: FiltrosDeEstudo) => FiltrosDeEstudo) => {
    escreverFiltros(ajuste(aplicados));
    void queryClient.invalidateQueries({ queryKey: ['study-questions'] });
  };

  const removerValor = (campo: CampoLista, valor: string) =>
    reaplicarSem((filtros) => ({
      ...filtros,
      [campo]: (filtros[campo] ?? []).filter((atual) => atual !== valor),
    }));

  const removerDisciplina = () =>
    reaplicarSem((filtros) => ({
      ...filtros,
      disciplineId: undefined,
      subjectIds: [],
      subjectDetailIds: [],
    }));

  const removerAssunto = (id: string) =>
    reaplicarSem((filtros) => ({
      ...filtros,
      subjectIds: (filtros.subjectIds ?? []).filter((atual) => atual !== id),
      subjectDetailIds: (filtros.subjectDetailIds ?? []).filter((atual) => atual !== id),
    }));

  const aplicar = () => {
    escreverFiltros(rascunho);
    setGavetaAberta(false);

    /*
     * Buscar de novo é sempre uma consulta nova.
     *
     * Com os mesmos filtros a chave do cache não muda, e o React Query
     * devolveria o resultado guardado - o botão parecia não fazer nada quando
     * havia questão publicada nesse meio-tempo. Quem clica em "Buscar" está
     * pedindo o estado atual.
     */
    void queryClient.invalidateQueries({ queryKey: ['study-questions'] });
  };

  const limpar = () => {
    setRascunho({});
    escreverFiltros({});
    setGavetaAberta(false);
  };

  const contar = (arr?: string[]) => arr?.length ?? 0;
  const quantidadeDeFiltros =
    contar(aplicados.years) +
    contar(aplicados.editions) +
    contar(aplicados.boardIds) +
    contar(aplicados.notaryOfficeIds) +
    contar(aplicados.essayKinds) +
    (aplicados.disciplineId ? 1 : 0) +
    contar(aplicados.subjectIds) +
    contar(aplicados.subjectDetailIds);

  const limparLink = () => filtrar({ questoes: undefined, page: undefined });

  /*
   * Quantas do link ficaram de fora - anulada ainda não publicada, por
   * exemplo. Só se conta sem outro filtro por cima: com filtro, a diferença
   * seria o filtro, e não a disponibilidade.
   */
  const pedidasNoLink = new Set(questoesDoLink).size;
  const faltamNoLink =
    consulta.data && quantidadeDeFiltros === 0 && !(leiCitada && artigoCitado)
      ? Math.max(0, pedidasNoLink - consulta.data.total)
      : 0;

  const aoBloquear = (motivo: MotivoDeBloqueio, origem: 'answer' | 'commentary') =>
    bloquear({ motivo, origem, chave: module });

  const painel = (
    <PainelDeFiltros
      rascunho={rascunho}
      onMudarLista={mudarLista}
      onMudarDisciplina={mudarDisciplina}
      onMudarAssuntos={mudarAssuntos}
      onRemoverValor={removerValor}
      onRemoverDisciplina={removerDisciplina}
      onRemoverAssunto={removerAssunto}
      onAplicar={aplicar}
      onLimpar={limpar}
      aplicados={aplicados}
      // Banca só no ENAC hoje é a FGV: sem uma escolha a fazer, o filtro sai.
      mostrarBanca={module !== 'enac'}
      // Estado (cartório) só faz sentido nas provas estaduais: o ENAC é nacional.
      mostrarCartorio={module !== 'enac'}
      // Classificação não é filtro nas discursivas (o dado segue no admin).
      mostrarClassificacao={module !== 'essay'}
      // Tipo (dissertação/peça/questão) só faz sentido na 2ª fase.
      mostrarTipoDiscursiva={module === 'essay'}
      // Há dois ENACs por ano: no ENAC, o filtro de tempo é a edição.
      porSemestre={module === 'enac'}
      compacto={!telas.md}
    />
  );

  /*
   * O módulo existe e ainda não abre.
   *
   * A barra lateral já não deixa chegar aqui, mas o endereço é digitável e o
   * histórico do navegador guarda visitas antigas - e sem esta porta a pessoa
   * cairia numa lista com "Encontramos 0 questões", que parece defeito. Dizer
   * o motivo custa uma tela e evita um chamado.
   */
  if (moduloEmPreparo(module)) {
    return (
      <ModuleThemeProvider module={module}>
        <Result
          /* O ícone do `Result` nasce com 72px, e um cadeado desse tamanho
             vira o assunto da tela no lugar do texto que explica o motivo. */
          icon={<LockOutlined style={{ fontSize: 44 }} />}
          title={`${moduleLabel[module]} está sendo comentada`}
          subTitle="Estamos escrevendo a resolução de cada questão antes de abrir o módulo - questão sem comentário não ajuda ninguém a passar. Enquanto isso, o Enac está completo."
          extra={
            <Button type="primary" onClick={() => void navegar('/estudar?module=enac')}>
              Ir para o Enac
            </Button>
          }
        />
      </ModuleThemeProvider>
    );
  }

  return (
    <ModuleThemeProvider module={module}>
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        {/* A troca de módulo mora na barra lateral; aqui basta a identidade. */}
        <CabecalhoDoModulo
          module={module}
          titulo={moduleLabel[module]}
          status={status.data}
          aoVerPlanos={() => abrirOferta({ chave: module })}
        />

        {telas.md ? (
          /*
            Sem título: o cartão dizia "Filtrar questões" em cima de três campos
            e de um botão escrito "Buscar questões" - a mesma frase duas vezes,
            e uma barra de título inteira para não dizer nada que os campos já
            não digam. Na gaveta do celular o título fica, porque lá ele é o
            cabeçalho da folha que abriu.
          */
          <Card size="small">{painel}</Card>
        ) : (
          <>
            <Badge count={quantidadeDeFiltros}>
              <Button icon={<FilterOutlined />} onClick={() => setGavetaAberta(true)}>
                Filtros
              </Button>
            </Badge>
            <Drawer
              title="Filtrar questões"
              placement="bottom"
              size="auto"
              open={gavetaAberta}
              onClose={() => setGavetaAberta(false)}
            >
              {painel}
            </Drawer>
          </>
        )}

        {consulta.error ? (
          <Alert type="error" showIcon title={(consulta.error as Error).message} />
        ) : null}

        {consulta.isPending || !consulta.data ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : consulta.data.items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              questoesDoLink.length > 0
                ? 'As questões deste link ainda não estão disponíveis.'
                : leiCitada && artigoCitado
                  ? `Nenhuma questão publicada deste módulo cobra o art. ${artigoCitado} da Lei ${leiCitada}.`
                  : quantidadeDeFiltros > 0
                    ? 'Nenhuma questão com esses filtros.'
                    : 'Ainda não há questões publicadas neste módulo.'
            }
          >
            {/* Sem saída, o link vazio seria um beco: a lista inteira fica a um clique. */}
            {questoesDoLink.length > 0 ? (
              <Button onClick={limparLink}>Ver todas as questões</Button>
            ) : null}
          </Empty>
        ) : (
          <>
            {/*
              * **O recorte que veio de fora precisa se explicar.** Chegando pela
              * etiqueta da Legislação Comentada, a lista encurta sem que nada na
              * tela diga por quê - e uma lista curta sem motivo se lê como
              * "acabaram as questões". A etiqueta diz o artigo e é ela que
              * desfaz o recorte.
              */}
            {leiCitada && artigoCitado ? (
              <Alert
                type="info"
                showIcon
                title={`Questões que cobram o art. ${artigoCitado} da Lei ${leiCitada}`}
                action={
                  <Button
                    size="small"
                    onClick={() =>
                      filtrar({ legislation: undefined, artigo: undefined, page: undefined })
                    }
                  >
                    Ver todas
                  </Button>
                }
              />
            ) : null}

            {/*
              * O mesmo vale para o link que escolheu as questões: a lista curta
              * se explica, e o "Ver todas" desfaz o recorte.
              *
              * **O texto é fixo.** Nada da URL vira frase na tela - só a
              * contagem -, então um link forjado não põe palavra nossa na boca
              * de ninguém.
              */}
            {questoesDoLink.length > 0 ? (
              <Alert
                type="info"
                showIcon
                title="Você está vendo as questões escolhidas pelo link."
                description={
                  faltamNoLink > 0
                    ? `${formatador.format(faltamNoLink)} das ${formatador.format(pedidasNoLink)} questões do link ainda não ${faltamNoLink === 1 ? 'está disponível' : 'estão disponíveis'}.`
                    : undefined
                }
                action={
                  <Button size="small" onClick={limparLink}>
                    Ver todas
                  </Button>
                }
              />
            ) : null}

            <Flex align="center" justify="space-between" gap={12} wrap>
              <Typography.Text strong>
                {/* O link de uma questão só, o de cada número do cronograma, acha exatamente 1. */}
                Encontramos {formatador.format(consulta.data.total)}{' '}
                {consulta.data.total === 1 ? 'questão' : 'questões'}
              </Typography.Text>
              <Space size={8}>
                <Typography.Text type="secondary">Por página</Typography.Text>
                <Select
                  value={porPagina}
                  style={{ width: 88 }}
                  onChange={(valor) => filtrar({ porPagina: valor })}
                  options={TAMANHOS_DE_PAGINA.map((valor) => ({ value: valor, label: valor }))}
                />
              </Space>
            </Flex>

            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {consulta.data.items.map((questao) => (
                <QuestaoCard key={questao.id} questao={questao} onBloqueio={aoBloquear} />
              ))}
            </Space>

            <Pagination
              align="center"
              current={pagina}
              pageSize={porPagina}
              total={consulta.data.total}
              showSizeChanger={false}
              hideOnSinglePage
              // Dezenas de páginas não cabem numeradas num celular.
              simple={!telas.md}
              onChange={(proxima) => {
                irParaPagina(proxima);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </>
        )}
      </Space>

    </ModuleThemeProvider>
  );
}
